import type { ComponentRef } from '@wix/editor-platform-sdk-types'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type {
  AppApi,
  BindingConfig,
  FieldBindingConfig,
} from '@wix/wix-data-client-common'
import { isEmpty } from 'lodash'
import { BINDING_TYPES } from '../../../business-logic/bindings/Binding'
import { remapFieldBindings } from './remapFieldBindings'

const adjustComponentBindings = async (
  componentRef: ComponentRef,
  controllerRef: ComponentRef,
  newBindings: unknown[],
  role: string,
  appApi: AppApi,
) => {
  if (newBindings.length === 0) {
    await appApi.disconnectBindingSection({
      componentRef,
      controllerRef,
      role,
    })
  } else {
    await appApi.connectBindingSection({
      componentRef,
      controllerRef,
      newBindings: newBindings as any[],
      role,
      requiresPrimaryConnection: true,
      isUserInputFilter: false,
      newColumns: [],
    })
  }
}

const extractFieldBindings = (bindings: BindingConfig[]) =>
  bindings.filter(b => b.type === BINDING_TYPES.Field) as FieldBindingConfig[]

const extractNonFieldBindings = (bindings: BindingConfig[]) =>
  bindings.filter(b => b.type !== BINDING_TYPES.Field)

export const rebindComponentsToAnotherCollection = async ({
  fieldMap,
  controllerRef,
  newCollectionId,
  collectionsApi,
  appApi,
}: {
  fieldMap: Record<string, string>
  controllerRef: ComponentRef
  newCollectionId: string
  collectionsApi: CollectionsApi
  appApi: AppApi
}) => {
  const [connections, newSchema] = await Promise.all([
    appApi.fetchControllerBindings({
      controllerRef,
    }),
    collectionsApi.get(newCollectionId),
  ])

  for (const controllerBinding of connections) {
    const { role, bindings, componentRef, componentType } = controllerBinding

    const fieldBindings = extractFieldBindings(bindings)

    if (isEmpty(fieldBindings)) {
      continue
    }

    const bindingDefinition = await appApi.getBindingDefinition({
      componentType,
    })

    const newFieldBindings = remapFieldBindings(
      fieldBindings,
      fieldMap,
      newSchema,
      bindingDefinition,
      role,
    )

    const nonFieldBindings = extractNonFieldBindings(bindings)
    const mergedBindings = [...nonFieldBindings, ...newFieldBindings]

    await adjustComponentBindings(
      componentRef,
      controllerRef,
      mergedBindings,
      role,
      appApi,
    )
  }
}
